import type { EntitlementStatus } from "../../api/types";

/**
 * Severity for the app-level banner and the inline capacity notices below.
 * Mirrors `BannerTone` (components/Banner.tsx) so callers can pass one
 * straight through, but is defined independently here — this file has no
 * business importing a component.
 */
export type EntitlementSeverity = "info" | "warning" | "danger";

export interface EntitlementBannerSpec {
  severity: EntitlementSeverity;
  title: string;
  description: string;
}

/**
 * Maps an entitlement status to the app-level degraded-state banner an
 * operator should see, or `null` for "say nothing." This is the ONE place
 * that decides severity per `LicenseState` — worth being deliberate about,
 * because getting it wrong in either direction is the actual failure mode:
 * crying wolf on a harmless state trains people to ignore the banner, and
 * staying quiet on a real one hides it.
 *
 *  - `active`   — fully licensed. Nothing to say: null.
 *  - `stale`    — NOT alarming. The tenant is still fully licensed; we
 *    simply failed to phone home to recheck. `info` only, and the
 *    description explicitly says the plan is unaffected — the wording is
 *    the whole point, not just the color.
 *  - `expired`  — real: the tenant is now on the Free fallback. `warning`.
 *  - `absent`   — normal for a fresh self-hosted install (D-4: we never nag
 *    a customer who never bought a license). Only worth a banner once the
 *    tenant is near/at the Free ceiling, where it becomes actionable —
 *    see `isNearOrAtNodeLimit`.
 *  - `invalid`  — always shown, and the one state escalated to `danger`: a
 *    signature/tamper/wrong-tenant mismatch may mean something beyond
 *    licensing economics is wrong, so silence here is the dangerous choice.
 */
export function entitlementBanner(status: EntitlementStatus): EntitlementBannerSpec | null {
  switch (status.state) {
    case "active":
      return null;

    case "stale":
      return {
        severity: "info",
        title: "Couldn't recheck your license",
        description: `${status.message} Your plan is unaffected.`,
      };

    case "expired":
      return {
        severity: "warning",
        title: "Your license has expired",
        description: status.message,
      };

    case "absent":
      if (!isNearOrAtNodeLimit(status)) return null;
      return {
        severity: "warning",
        title: "Approaching the Free plan's node limit",
        description: status.message,
      };

    case "invalid":
      return {
        severity: "danger",
        title: "Your license failed verification",
        description: status.message,
      };
  }
}

const NEAR_LIMIT_RATIO = 0.8;

/** True once node usage is at 80% of the licensed ceiling or higher (before
 * grace is even considered) — the threshold `entitlementBanner` uses to
 * decide an `absent` (Free, no license) tenant is worth interrupting for. */
function isNearOrAtNodeLimit(status: EntitlementStatus): boolean {
  const ratio = nodeUsageRatio(status);
  return ratio !== null && ratio >= NEAR_LIMIT_RATIO;
}

function nodeUsageRatio(status: EntitlementStatus): number | null {
  const usage = status.usage.nodes;
  if (!usage || usage.unlimited || usage.used === null || !usage.limit) return null;
  return usage.used / usage.limit;
}

export interface NodeCapacityNotice {
  severity: EntitlementSeverity;
  message: string;
}

/**
 * The inline "approaching/at capacity" copy for the Nodes list and the
 * Add-a-host flow — deliberately independent of `entitlementBanner`/
 * `LicenseState`: an admin adding a host cares about "how much room is
 * left" the same way whether they're on a paid plan, Free, or a stale
 * refresh, so this reads only `nodeLimit`/`usage.nodes`. Returns `null`
 * when there's nothing worth saying (comfortably under the ceiling, or
 * unlimited/unknown usage).
 */
export function nodeCapacityNotice(status: EntitlementStatus): NodeCapacityNotice | null {
  const usage = status.usage.nodes;
  if (!usage || usage.unlimited || usage.used === null || usage.limit === undefined) return null;

  const { used, limit } = usage;
  const grace = usage.grace ?? 0;
  const effective = limit + grace;

  if (used >= effective) {
    return {
      severity: "danger",
      message:
        `You're using all ${used} of your ${limit}${grace > 0 ? ` (plus ${grace} grace)` : ""} licensed nodes. ` +
        "Adding another host will be refused until you free one up or raise your plan.",
    };
  }

  if (used >= limit) {
    const over = used - limit;
    return {
      severity: "warning",
      message:
        `You're using ${used} of ${limit} licensed nodes — the extra ${over} ${over === 1 ? "is" : "are"} ` +
        "covered by grace capacity. Raise your plan from Settings → Billing before grace runs out.",
    };
  }

  if (used / limit >= NEAR_LIMIT_RATIO) {
    return {
      severity: "info",
      message: `You're using ${used} of ${limit} licensed nodes.`,
    };
  }

  return null;
}

/** Human labels for the feature slugs `entitlements.Feature.Name()` returns
 * (server/internal/entitlements/entitlements.go — keep in sync). Falls back
 * to a title-cased rendering of the raw slug for any feature the server
 * adds before this map is updated, so a new feature never renders as
 * literally nothing. */
const FEATURE_LABELS: Record<string, string> = {
  rollback: "Rollback",
  health_gate: "Health gates",
  secrets: "Encrypted secrets",
  rbac: "Role-based access control",
  sso: "SSO / OIDC login",
  audit_log: "Audit trail",
  cve_detection: "Vulnerability detection",
  auto_promote: "Automatic promotion",
  safe_auto_updates: "Safe auto-updates",
  alert_integrations: "Alert integrations",
  unlimited_envs: "Unlimited environments",
  approvals: "Deployment approvals",
  preview_envs: "Preview environments",
  per_env_rbac: "Per-environment RBAC",
  vuln_policy_gates: "Vulnerability policy gates",
  gitops_multi_repo: "Multi-repo GitOps",
  volume_backups: "Scheduled volume backups",
  exec_session_recording: "Exec session recording",
  audit_streaming: "Audit log streaming (SIEM)",
  scim: "SCIM user provisioning",
};

export function humanizeFeatureName(name: string): string {
  return (
    FEATURE_LABELS[name] ??
    name
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}
